import { useContext, useEffect } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { ApiContext } from '@shared/contexts';
import { TERMINOLOGY } from '@shared/i18n/terminology';
import { useOwnedDocument } from '@modules/documents/api';
import { useDocumentSavingStore } from '@modules/documents/stores/useDocumentSavingStore';
import {
  CHANGE_TEMPLATES_ENDPOINT,
  CHANGE_TEMPLATES_QUERY_KEY,
  CHANGE_TEMPLATE_DOCUMENT_QUERY_KEY,
  CHANGE_TEMPLATE_QUERY_KEY,
} from './constants';
import type {
  TChangeTemplateDetails,
  TChangeTemplateList,
  TChangeTemplateDraftAssessment,
  TChangeTemplateListParams,
  TCompleteChangeTemplateInput,
  TCreateChangeTemplateAssessmentInput,
  TCreateChangeTemplateInput,
  TUpdateChangeTemplateAssessmentInput,
} from './types';

// Backend stores oldest-first; reverse so versions[0] is the latest completed version.
const reverseVersions = (details: TChangeTemplateDetails) => {
  details.changeTemplate.versions.reverse();
  return details;
};

const updateDraftAssessments = (
  queryClient: QueryClient,
  id: string,
  update: (assessments: TChangeTemplateDraftAssessment[]) => TChangeTemplateDraftAssessment[]
) =>
  queryClient.setQueryData<TChangeTemplateDetails>([CHANGE_TEMPLATE_QUERY_KEY, id], (details) =>
    details?.changeTemplate.draft
      ? {
          ...details,
          changeTemplate: {
            ...details.changeTemplate,
            draft: { ...details.changeTemplate.draft, assessments: update(details.changeTemplate.draft.assessments) },
          },
        }
      : details
  );

export const useChangeTemplates = (params: TChangeTemplateListParams) => {
  const { apiClient, useHandleQueryError, retry } = useContext(ApiContext);
  const query = useQuery<TChangeTemplateList>({
    queryKey: [CHANGE_TEMPLATES_QUERY_KEY, params],
    retry,
    placeholderData: keepPreviousData,
    queryFn: async ({ signal }) =>
      (
        await apiClient.get<TChangeTemplateList>(CHANGE_TEMPLATES_ENDPOINT, {
          signal,
          params: { ...params, page: params.page - 1 },
        })
      ).data,
  });
  useHandleQueryError(query.isError, query.error, `Loading ${TERMINOLOGY.changeTemplate.lowerPlural} failed!`, true);
  return query;
};

export const useChangeTemplate = (id: string, enabled: boolean) => {
  const { apiClient, useHandleQueryError, retry } = useContext(ApiContext);
  const query = useQuery<TChangeTemplateDetails>({
    queryKey: [CHANGE_TEMPLATE_QUERY_KEY, id],
    enabled,
    retry,
    queryFn: async ({ signal }) =>
      reverseVersions(
        (await apiClient.get<TChangeTemplateDetails>(`${CHANGE_TEMPLATES_ENDPOINT}/${id}`, { signal })).data
      ),
  });
  useHandleQueryError(query.isError, query.error, `Loading the ${TERMINOLOGY.changeTemplate.lower} failed!`, true);
  return query;
};

export const useChangeTemplateDocument = (id: string, documentId: string | undefined, templateVersion?: string) =>
  useOwnedDocument({
    queryKey: [CHANGE_TEMPLATE_DOCUMENT_QUERY_KEY, id, documentId, templateVersion],
    documentId,
    url: `${CHANGE_TEMPLATES_ENDPOINT}/${id}/documents/${documentId}`,
    errorMessage: `Loading the ${TERMINOLOGY.changeTemplate.lower} document failed!`,
    params: { version: templateVersion },
  });

// Assessment titles, dates and authors come from the documents, so leaving a document that saved refetches them.
export const useRefetchChangeTemplateAfterSave = (id: string) => {
  const queryClient = useQueryClient();
  useEffect(() => {
    const savedAt = useDocumentSavingStore.getState().lastSavedAt;
    return () => {
      if (useDocumentSavingStore.getState().lastSavedAt !== savedAt)
        queryClient.invalidateQueries({ queryKey: [CHANGE_TEMPLATE_QUERY_KEY, id] });
    };
  }, [id]);
};

export const useCreateChangeTemplate = () => {
  const { apiClient, handleApiError, show } = useContext(ApiContext);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: TCreateChangeTemplateInput) =>
      (await apiClient.post<string>(CHANGE_TEMPLATES_ENDPOINT, input)).data,
    onError: handleApiError(`Creating the ${TERMINOLOGY.changeTemplate.lower} failed!`, true),
    onSuccess: () => {
      show('success', `${TERMINOLOGY.changeTemplate.upper} created.`);
      queryClient.invalidateQueries({ queryKey: [CHANGE_TEMPLATES_QUERY_KEY] });
    },
  });
};

export const useUpdateChangeTemplateTitle = (id: string) => {
  const { apiClient, handleApiError, show } = useContext(ApiContext);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (title: string) =>
      reverseVersions(
        (await apiClient.put<TChangeTemplateDetails>(`${CHANGE_TEMPLATES_ENDPOINT}/${id}/title`, { title })).data
      ),
    onError: handleApiError(`Renaming the ${TERMINOLOGY.changeTemplate.lower} failed!`, true),
    onSuccess: (details) => {
      show('success', `${TERMINOLOGY.changeTemplate.upper} renamed.`);
      queryClient.setQueryData([CHANGE_TEMPLATE_QUERY_KEY, id], details);
    },
  });
};

export const useUpdateChangeTemplateDescription = (id: string) => {
  const { apiClient, handleApiError, show } = useContext(ApiContext);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (description: string | null) =>
      reverseVersions(
        (await apiClient.put<TChangeTemplateDetails>(`${CHANGE_TEMPLATES_ENDPOINT}/${id}/description`, { description }))
          .data
      ),
    onError: handleApiError('Saving the description failed!', true),
    onSuccess: (details) => {
      show('success', 'Description saved.');
      queryClient.setQueryData([CHANGE_TEMPLATE_QUERY_KEY, id], details);
    },
  });
};

export const useCreateChangeTemplateAssessment = (id: string) => {
  const { apiClient, handleApiError, show } = useContext(ApiContext);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: TCreateChangeTemplateAssessmentInput) =>
      (await apiClient.post<TChangeTemplateDraftAssessment>(`${CHANGE_TEMPLATES_ENDPOINT}/${id}/assessments`, input))
        .data,
    onError: handleApiError('Creating the assessment failed!', true),
    onSuccess: (assessment) => {
      show('success', 'Assessment created.');
      updateDraftAssessments(queryClient, id, (assessments) => [...assessments, assessment]);
    },
  });
};

export const useUpdateChangeTemplateAssessment = (id: string) => {
  const { apiClient, handleApiError, show } = useContext(ApiContext);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ assessmentId, ...input }: TUpdateChangeTemplateAssessmentInput & { assessmentId: string }) =>
      reverseVersions(
        (
          await apiClient.put<TChangeTemplateDetails>(
            `${CHANGE_TEMPLATES_ENDPOINT}/${id}/assessments/${assessmentId}`,
            input
          )
        ).data
      ),
    onError: handleApiError('Updating the assessment failed!', true),
    onSuccess: (details) => {
      show('success', 'Assessment updated.');
      queryClient.setQueryData([CHANGE_TEMPLATE_QUERY_KEY, id], details);
    },
  });
};

export const useReorderChangeTemplateAssessments = (id: string) => {
  const { apiClient, handleApiError } = useContext(ApiContext);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (assessmentIds: string[]) =>
      apiClient.put<void>(`${CHANGE_TEMPLATES_ENDPOINT}/${id}/assessments/order`, { assessmentIds }),
    onError: handleApiError('Reordering the assessments failed!', true),
    // Success and a changed list both need the server's order before another drag.
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: [CHANGE_TEMPLATE_QUERY_KEY, id] });
    },
  });
};

export const useDeleteChangeTemplateAssessment = (id: string) => {
  const { apiClient, handleApiError, show } = useContext(ApiContext);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (assessmentId: string) =>
      apiClient.delete<void>(`${CHANGE_TEMPLATES_ENDPOINT}/${id}/assessments/${assessmentId}`),
    onError: handleApiError('Removing the assessment failed!', true),
    onSuccess: (_, assessmentId) => {
      show('success', 'Assessment removed.');
      updateDraftAssessments(queryClient, id, (assessments) =>
        assessments.filter((assessment) => assessment._id !== assessmentId)
      );
    },
  });
};

export const useCompleteChangeTemplate = (id: string) => {
  const { apiClient, handleApiError, show } = useContext(ApiContext);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: TCompleteChangeTemplateInput) =>
      reverseVersions(
        (await apiClient.put<TChangeTemplateDetails>(`${CHANGE_TEMPLATES_ENDPOINT}/${id}/complete`, input)).data
      ),
    onError: handleApiError(`Completing the ${TERMINOLOGY.changeTemplate.lower} failed!`, true),
    onSuccess: (details) => {
      show('success', `Completed draft and created version ${details.changeTemplate.versions[0].id}.`);
      queryClient.setQueryData([CHANGE_TEMPLATE_QUERY_KEY, id], details);
      // Completing turns each document's draft into a version, so a shown document is out of date.
      queryClient.invalidateQueries({ queryKey: [CHANGE_TEMPLATE_DOCUMENT_QUERY_KEY, id] });
    },
  });
};

export const useCreateChangeTemplateDraft = (id: string) => {
  const { apiClient, handleApiError } = useContext(ApiContext);
  const queryClient = useQueryClient();
  return useMutation<TChangeTemplateDetails, Error, void>({
    mutationFn: async () =>
      reverseVersions((await apiClient.put<TChangeTemplateDetails>(`${CHANGE_TEMPLATES_ENDPOINT}/${id}/draft`)).data),
    onError: handleApiError('Creating the draft failed!', true),
    onSuccess: (details) => {
      queryClient.setQueryData([CHANGE_TEMPLATE_QUERY_KEY, id], details);
      // A shown document was loaded without a draft, so with read access only.
      queryClient.invalidateQueries({ queryKey: [CHANGE_TEMPLATE_DOCUMENT_QUERY_KEY, id] });
    },
  });
};

export const useArchiveChangeTemplate = () => {
  const { apiClient, handleApiError, show } = useContext(ApiContext);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.delete(`${CHANGE_TEMPLATES_ENDPOINT}/${id}`),
    onError: (error, id, context) => {
      handleApiError(`Archiving the ${TERMINOLOGY.changeTemplate.lower} failed!`, true)(error, id, context);
      // The refusal may mean the right was lost: read the current right again.
      queryClient.invalidateQueries({ queryKey: [CHANGE_TEMPLATE_QUERY_KEY, id] });
      queryClient.invalidateQueries({ queryKey: [CHANGE_TEMPLATES_QUERY_KEY] });
    },
    onSuccess: (_, id) => {
      queryClient.removeQueries({ queryKey: [CHANGE_TEMPLATE_QUERY_KEY, id] });
      queryClient.invalidateQueries({ queryKey: [CHANGE_TEMPLATES_QUERY_KEY] });
      show('success', `${TERMINOLOGY.changeTemplate.upper} archived.`);
    },
  });
};
